import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useStore } from "../../zustand/store";
import { getNameInitials, assetsUrlPath, dateFormatter, hitApi } from "../../components/commonAction";
import svg from "../../components/svg";
import WorldHeatMap from "../../components/WorldHeatMap";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/admin-table.css";
import "../../styles/admin-dashboard.css";
import "./userDetails.css";

countries.registerLocale(enLocale);

// Resolve a clean, properly-cased country name. Backend may return the value
// in upper-case (e.g. "INDIA") — prefer the ISO library's proper name when we
// have a code, otherwise title-case the raw string.
const prettyCountry = (row) => {
    const code = (row?.code || "").toUpperCase();
    if (code.length === 2) {
        const name = countries.getName(code, "en");
        if (name) return name;
    }
    if (code.length === 3) {
        const a2 = countries.alpha3ToAlpha2(code);
        const name = a2 && countries.getName(a2, "en");
        if (name) return name;
    }
    const raw = row?.country || code || "";
    return raw.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

// ── Inline SVG icons ───────────────────────────────────────────────────────────
const IconUsers       = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconCreditCard  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
const IconUserFree    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconActivity    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
const IconUserX       = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="18" y1="8" x2="23" y2="13"/><line x1="23" y1="8" x2="18" y2="13"/></svg>;
const IconShieldCheck = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>;
const IconShieldOff   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19.69 14a6.9 6.9 0 0 0 .31-2V5l-8-3-3.16 1.18"/><path d="M4.73 4.73L4 5v7c0 6 8 10 8 10a20.29 20.29 0 0 0 5.62-4.38"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;
const IconCalendar    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IconDollarSign  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const IconCheckCircle = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const IconClock       = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IconRefreshCw   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
const IconRepeat      = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>;
const IconShoppingBag = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>;
const IconAlertCircle = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
const IconTrendingUp  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
const IconTrendingDown = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>;
const IconArrowRight   = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg>;
const IconClose        = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconUserCheck   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>;
const IconBell        = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
const IconAward       = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>;

// ── Helper components ──────────────────────────────────────────────────────────

function StatCard({ label, value, icon, money, accent, trend, inverse }) {
    // trend = { pct: 12.4, dir: 'up' | 'down' }
    // inverse = true for metrics where "up" is undesirable (failed payments, inactive users)
    const isBad = trend && ((trend.dir === 'up' && inverse) || (trend.dir === 'down' && !inverse));
    return (
        <div className={`pxl-stat-card${money ? ' pxl-stat-card--money' : ''}${accent ? ` pxl-stat-card--accent-${accent}` : ''}`}>
            <div className="pxl-stat-card-header">
                <span className="pxl-stat-card-label">{label}</span>
                <span className={`pxl-stat-card-icon${money ? ' pxl-stat-card-icon--money' : ''}`}>{icon}</span>
            </div>
            <div className="pxl-stat-card-value">{value}</div>
            {trend && (
                <div
                    className={`pxl-stat-trend pxl-stat-trend--${trend.dir}${isBad ? ' is-bad' : ''}`}
                    title="vs previous period"
                >
                    {trend.dir === 'up' ? <IconTrendingUp /> : <IconTrendingDown />}
                    <span>{Math.abs(trend.pct).toFixed(1)}%</span>
                    <span className="pxl-stat-trend-period">vs prev.</span>
                </div>
            )}
        </div>
    );
}

function SourceBar({ label, count, total }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="pxl-source-row">
            <div className="pxl-source-meta">
                <span className="pxl-source-name">{label}</span>
                <span className="pxl-source-count">{count.toLocaleString()}</span>
            </div>
            <div className="pxl-source-bar-track">
                <div className="pxl-source-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="pxl-source-pct">{pct}%</div>
        </div>
    );
}

function SectionPanel({ title, subtitle, count, countTone = 'warning', countLabel = 'pending', children, headerRight }) {
    return (
        <div className="pxl-ud-panel pxl-adm-panel-mb">
            <div className="pxl-ud-panel-header">
                <div>
                    <h5 className="pxl-ud-panel-title">
                        {title}
                        {count != null && count > 0 && (
                            <span className={`pxl-adm-panel-count pxl-ud-pill pxl-ud-pill--${countTone}`}>
                                {count} {countLabel}
                            </span>
                        )}
                    </h5>
                    {subtitle && <p className="pxl-ud-panel-sub">{subtitle}</p>}
                </div>
                {headerRight && <div>{headerRight}</div>}
            </div>
            {children}
        </div>
    );
}

const BILLING_STATUS_MAP = {
    completed: { label: 'Completed', cls: 'pxl-ud-pill--success' },
    pending:   { label: 'Pending',   cls: 'pxl-ud-pill--warning' },
    failed:    { label: 'Failed',    cls: 'pxl-ud-pill--danger'  },
    refunded:  { label: 'Refunded',  cls: 'pxl-ud-pill--muted'   },
};

function StatusPill({ status }) {
    const cfg = BILLING_STATUS_MAP[status] || { label: status || '—', cls: 'pxl-ud-pill--muted' };
    return <span className={`pxl-ud-pill ${cfg.cls}`}>{cfg.label}</span>;
}

function LoadingPanel({ label }) {
    return (
        <div className="pxl-adm-loading">
            <span className="pxl-adm-spinner" aria-hidden="true" />
            <span>{label || 'Loading…'}</span>
        </div>
    );
}

function EmptyRow({ text }) {
    return (
        <div className="pxl-adm-empty">{text || 'No data.'}</div>
    );
}

function UserCell({ name, email, profilePic }) {
    return (
        <div className="pxl-user-cell">
            <div className="pxl-user-avatar">
                {profilePic
                    ? <img src={assetsUrlPath(profilePic)} alt={name} />
                    : getNameInitials(name || '?')}
            </div>
            <div className="pxl-user-info">
                <p className="pxl-user-name">{name || '—'}</p>
                <p className="pxl-user-email">{email || '—'}</p>
            </div>
        </div>
    );
}

function DashboardHeader({ title, subtitle, range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, onRefresh, refreshing }) {
    const RANGES = [
        { id: 'today', label: 'Today' },
        { id: '7d',    label: 'Last 7 days' },
        { id: '30d',   label: 'Last 30 days' },
        { id: 'custom',label: 'Custom' },
    ];
    return (
        <div className="pxl-adm-page-head">
            <div className="pxl-adm-page-title-block">
                <h2 className="pxl-adm-page-title">{title}</h2>
                {subtitle && <p className="pxl-adm-page-sub">{subtitle}</p>}
            </div>
            <div className="pxl-adm-page-actions">
                <div className="pxl-adm-range-seg" role="tablist" aria-label="Date range">
                    {RANGES.map(r => (
                        <button
                            key={r.id}
                            type="button"
                            role="tab"
                            aria-selected={range === r.id}
                            className={`pxl-adm-range-btn${range === r.id ? ' is-active' : ''}`}
                            onClick={() => setRange(r.id)}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
                {range === 'custom' && (
                    <div className="pxl-adm-range-custom">
                        <DatePicker
                            selected={customFrom}
                            onChange={d => setCustomFrom(d)}
                            selectsStart
                            startDate={customFrom}
                            endDate={customTo}
                            placeholderText="From"
                            dateFormat="dd MMM yyyy"
                            className="pxl-adm-range-date"
                            popperPlacement="bottom-end"
                            popperClassName="pxl-adm-dp-popper"
                            popperProps={{ strategy: 'fixed' }}
                        />
                        <span className="pxl-adm-range-dash">—</span>
                        <DatePicker
                            selected={customTo}
                            onChange={d => setCustomTo(d)}
                            selectsEnd
                            startDate={customFrom}
                            endDate={customTo}
                            minDate={customFrom}
                            placeholderText="To"
                            dateFormat="dd MMM yyyy"
                            className="pxl-adm-range-date"
                            popperPlacement="bottom-end"
                            popperClassName="pxl-adm-dp-popper"
                            popperProps={{ strategy: 'fixed' }}
                        />
                    </div>
                )}
                <div className="pxl-adm-refresh-wrap">
                    <button
                        type="button"
                        className={`pxl-adm-refresh${refreshing ? ' is-spinning' : ''}`}
                        onClick={onRefresh}
                        title="Refresh data"
                        aria-label="Refresh"
                    >
                        <IconRefreshCw />
                    </button>
                </div>
            </div>
        </div>
    );
}

function AlertsBanner({ alerts, onDismiss }) {
    if (!alerts || alerts.length === 0) return null;
    return (
        <div className="pxl-adm-alerts">
            {alerts.map(a => {
                const inner = (
                    <>
                        <span className="pxl-adm-alert-icon"><IconAlertCircle /></span>
                        <span className="pxl-adm-alert-text">{a.text}</span>
                        <span className="pxl-adm-alert-arrow"><IconArrowRight /></span>
                    </>
                );
                const cls = `pxl-adm-alert-chip pxl-adm-alert-chip--${a.tone}`;
                return (
                    <div key={a.id} className="pxl-adm-alert-wrap">
                        {a.to
                            ? <Link to={a.to} className={cls}>{inner}</Link>
                            : <button type="button" className={cls} onClick={a.onClick}>{inner}</button>}
                        <button
                            type="button"
                            className="pxl-adm-alert-dismiss"
                            onClick={() => onDismiss(a.id)}
                            title="Dismiss"
                            aria-label="Dismiss"
                        >
                            <IconClose />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

// ── Tab content ────────────────────────────────────────────────────────────────

function UsersTab({ data }) {
    if (!data) return <LoadingPanel label="Loading users data…" />;
    const s = data.stats || {};
    const t = data.trends || {};
    const bySource    = data.bySource    || [];
    const byCountry   = data.byCountry   || [];
    const countryMap  = data.countryMap  || [];
    const recentLogins = data.recentLogins || [];
    // WorldHeatMap accepts rows by ISO-2 code OR by country name; pass them through.
    const heatMapData = countryMap;
    return (
        <div className="pxl-adm-tab-content">
            <div className="pxl-stat-grid pxl-stat-grid--4">
                <StatCard label="Total Users"   value={(s.totalUsers || 0).toLocaleString()}    icon={<IconUsers />}      trend={t.totalUsers} />
                <StatCard label="Paid Users"    value={(s.paidUsers || 0).toLocaleString()}     icon={<IconCreditCard />} money trend={t.paidUsers} />
                <StatCard label="Free Users"    value={(s.freeUsers || 0).toLocaleString()}     icon={<IconUserFree />} />
                <StatCard label="Active"        value={(s.activeUsers || 0).toLocaleString()}   icon={<IconActivity />}   accent="green" trend={t.activeUsers} />
            </div>
            <div className="pxl-stat-grid pxl-stat-grid--4">
                <StatCard label="Inactive"       value={(s.inactiveUsers || 0).toLocaleString()}   icon={<IconUserX />}       accent="red" inverse />
                <StatCard label="Verified"       value={(s.verifiedUsers || 0).toLocaleString()}   icon={<IconShieldCheck />} accent="blue" />
                <StatCard label="Unverified"     value={(s.unverifiedUsers || 0).toLocaleString()} icon={<IconShieldOff />}   accent="orange" />
                <StatCard label="New This Month" value={(s.newThisMonth || 0).toLocaleString()}    icon={<IconCalendar />} />
            </div>

            <div className="pxl-adm-two-col">
                <SectionPanel title="Users by Source" subtitle="Signup channel breakdown">
                    {bySource.length > 0 ? (
                        <div className="pxl-source-list pxl-source-list--scroll">
                            {bySource.map(row => (
                                <SourceBar key={row.source} label={row.source} count={row.count} total={s.totalUsers} />
                            ))}
                        </div>
                    ) : (
                        <EmptyRow text="No source data yet." />
                    )}
                </SectionPanel>

                <SectionPanel title="Recent Logins" subtitle="10 most recent user sessions">
                    <div className="pxl-tbl-scroll pxl-adm-tbl-scroll--y">
                        <div className="pxl-users-table-wrap">
                            <table className="pxl_table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>User</th>
                                        <th>Last Login</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentLogins.length > 0 ? recentLogins.map((u, i) => (
                                        <tr key={u._id}>
                                            <td><p>{i + 1}</p></td>
                                            <td><UserCell name={u.name} email={u.email} profilePic={u.profilePic} /></td>
                                            <td><p className="pxl-adm-date-cell">{u.lastLoginAt ? dateFormatter(u.lastLoginAt, "D MMM YYYY, h:mm A") : '—'}</p></td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan={3}><EmptyRow text="No recent logins." /></td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </SectionPanel>
            </div>

            {byCountry.length > 0 && (
                <div className="pxl-adm-two-col pxl-adm-two-col--70-30">
                    <SectionPanel title="Users by Country" subtitle="Hover a country to see the user count">
                        <WorldHeatMap data={heatMapData} />
                    </SectionPanel>
                    <SectionPanel
                        title="Countries"
                        subtitle={`${byCountry.length} ${byCountry.length === 1 ? 'country' : 'countries'} with users`}
                    >
                        <div className="pxl-source-list pxl-source-list--scroll">
                            {byCountry.map(row => (
                                <SourceBar
                                    key={row.code || row.country}
                                    label={prettyCountry(row)}
                                    count={row.count}
                                    total={s.totalUsers}
                                />
                            ))}
                        </div>
                    </SectionPanel>
                </div>
            )}
        </div>
    );
}

function AppsTab({ apps }) {
    if (!apps) return <LoadingPanel label="Loading apps…" />;
    return (
        <div className="pxl-adm-tab-content">
            <SectionPanel title="All Apps" subtitle="Platform applications and their current status">
                <div className="pxl-tbl-scroll">
                    <div className="pxl-users-table-wrap">
                        <table className="pxl_table">
                            <thead>
                                <tr>
                                    <th>App</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apps.length > 0 ? apps.map(app => {
                                    const desc   = app.toolTip || app.description || '';
                                    const isLive = app.status === 1;
                                    return (
                                        <tr key={app._id || app.code}>
                                            <td>
                                                <div className="pxl-app-cell">
                                                    <div className={`pxl-app-icon-wrap pxl-dashboard-app-color-box-${app.icon || ''}`}>
                                                        {app.logoUrl
                                                            ? <img src={assetsUrlPath(app.logoUrl)} alt={app.name} />
                                                            : (svg.app && svg.app[app.icon]
                                                                ? svg.app[app.icon]
                                                                : <span className="pxl-app-icon-letter">{(app.name || '?').charAt(0).toUpperCase()}</span>)}
                                                    </div>
                                                    <span className="pxl-app-name">{app.name}</span>
                                                </div>
                                            </td>
                                            <td><p className="pxl-adm-desc-cell">{desc}</p></td>
                                            <td>
                                                <span className={`pxl-badge ${isLive ? 'pxl-badge--active' : 'pxl-badge--inactive'}`}>
                                                    {isLive ? 'Active' : 'Upcoming'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan={3}><EmptyRow text="No apps configured yet." /></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </SectionPanel>
        </div>
    );
}

function BillingTab({ data }) {
    if (!data) return <LoadingPanel label="Loading billing data…" />;
    const s = data.stats || {};
    const t = data.trends || {};
    const planRevenue = data.planRevenue || [];
    const transactions = data.recentTransactions || [];
    const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    const pendingTxnCount = transactions.filter(tx => tx.status === 'pending').length;

    return (
        <div className="pxl-adm-tab-content">
            <div className="pxl-stat-grid pxl-stat-grid--4">
                <StatCard label="Total Revenue"  value={fmt(s.totalRevenue)}                       icon={<IconDollarSign />}  money trend={t.totalRevenue} />
                <StatCard label="Completed"      value={(s.completedCount || 0).toLocaleString()}  icon={<IconCheckCircle />} accent="green" />
                <StatCard label="Pending"        value={(s.pendingCount   || 0).toLocaleString()}  icon={<IconClock />}       accent="orange" />
                <StatCard label="Refunded"       value={(s.refundedCount  || 0).toLocaleString()}  icon={<IconRefreshCw />}   accent="blue" />
            </div>
            <div className="pxl-stat-grid pxl-stat-grid--4">
                <StatCard label="Monthly Revenue"      value={fmt(s.monthlyRevenue)}                  icon={<IconCalendar />}    money />
                <StatCard label="Subscription Revenue" value={fmt(s.subscriptionRev)}                 icon={<IconRepeat />}      money />
                <StatCard label="One-time Revenue"     value={fmt(s.onetimeRev)}                      icon={<IconShoppingBag />} money />
                <StatCard label="Failed Payments"      value={(s.failedCount || 0).toLocaleString()}  icon={<IconAlertCircle />} accent="red" trend={t.failedCount} inverse />
            </div>

            <div className="pxl-adm-two-col pxl-adm-two-col--30-70">
                <SectionPanel title="Plan-wise Revenue" subtitle="Revenue by subscription tier">
                    <div className="pxl-tbl-scroll">
                        <div className="pxl-users-table-wrap">
                            <table className="pxl_table">
                                <thead>
                                    <tr>
                                        <th>Plan</th>
                                        <th>Txns</th>
                                        <th>Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {planRevenue.length > 0 ? planRevenue.map(row => (
                                        <tr key={row.tier}>
                                            <td><p className="pxl-ud-plan-name">{row.tier}</p></td>
                                            <td><p>{Number(row.transactions || 0).toLocaleString()}</p></td>
                                            <td><p className="pxl-ud-amount">{fmt(row.revenue)}</p></td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan={3}><EmptyRow text="No plan revenue yet." /></td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </SectionPanel>

                <SectionPanel
                    title="Transactions"
                    subtitle="Latest 10 transactions"
                    count={pendingTxnCount}
                    countTone="warning"
                    countLabel="pending"
                    headerRight={
                        <Link to="/admin/billing/transactions" className="pxl-adm-view-all">
                            View all
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/>
                            </svg>
                        </Link>
                    }
                >
                    <div className="pxl-tbl-scroll">
                        <div className="pxl-users-table-wrap">
                            <table className="pxl_table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Plan / Tier</th>
                                        <th>Type</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.length > 0 ? transactions.map(tx => (
                                        <tr key={tx._id}>
                                            <td><UserCell name={tx.user?.name} email={tx.user?.email} profilePic={null} /></td>
                                            <td><p className="pxl-ud-plan-name">{tx.tier?.name || '—'}</p></td>
                                            <td><span className="pxl-ud-type-tag">{tx.type}</span></td>
                                            <td><p className="pxl-ud-amount">${Number(tx.amount || 0).toFixed(2)}</p></td>
                                            <td><StatusPill status={tx.status} /></td>
                                            <td><p className="pxl-adm-date-cell">{dateFormatter(tx.createdAt, "D MMM YY")}</p></td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan={6}><EmptyRow text="No transactions yet." /></td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </SectionPanel>
            </div>
        </div>
    );
}

function AffiliatesTab({ data }) {
    if (!data) return <LoadingPanel label="Loading affiliates data…" />;
    const s = data.stats || {};
    const t = data.trends || {};
    const topEarner = data.topEarner;
    const toPay     = data.toPay || [];
    const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    const pendingAffiliateCount = toPay.filter(a => a.status === 'pending').length;
    return (
        <div className="pxl-adm-tab-content">
            <div className="pxl-stat-grid pxl-stat-grid--4">
                <StatCard label="Total Affiliates"  value={(s.totalAffiliates  || 0).toLocaleString()} icon={<IconUsers />}      trend={t.totalAffiliates} />
                <StatCard label="Active Affiliates" value={(s.activeAffiliates || 0).toLocaleString()} icon={<IconUserCheck />}  accent="green" />
                <StatCard label="Pending Payouts"   value={fmt(s.pendingPayouts)}                       icon={<IconClock />}      money inverse />
                <StatCard label="Pending Approvals" value={(s.pendingApprovals || 0).toLocaleString()} icon={<IconBell />}       accent="orange" />
            </div>

            <div className="pxl-stat-grid pxl-stat-grid--2">
                <StatCard label="Total Earnings This Month" value={fmt(s.mtdEarnings)} icon={<IconTrendingUp />} money />
                <div className="pxl-stat-card pxl-stat-card--highlight">
                    <div className="pxl-stat-card-header">
                        <span className="pxl-stat-card-label">Top Earner (All Time)</span>
                        <span className="pxl-stat-card-icon pxl-stat-card-icon--money"><IconAward /></span>
                    </div>
                    {topEarner ? (
                        <div className="pxl-adm-top-earner">
                            <UserCell name={topEarner.name} email={topEarner.email} profilePic={null} />
                            <div className="pxl-adm-top-earner-amount">{fmt(topEarner.amount)}</div>
                        </div>
                    ) : (
                        <EmptyRow text="No affiliate earnings yet." />
                    )}
                </div>
            </div>

            <SectionPanel
                title="Affiliates to Pay"
                subtitle="Top 10 by current balance — sorted highest to lowest"
                count={pendingAffiliateCount}
                countTone="warning"
                countLabel="pending"
            >
                <div className="pxl-tbl-scroll">
                    <div className="pxl-users-table-wrap">
                        <table className="pxl_table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Affiliate</th>
                                    <th>Referral Code</th>
                                    <th>Balance</th>
                                    <th>Total Earned</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {toPay.length > 0 ? toPay.map((aff, i) => (
                                    <tr key={aff._id}>
                                        <td><p>{i + 1}</p></td>
                                        <td><UserCell name={aff.user?.name} email={aff.user?.email} profilePic={null} /></td>
                                        <td><code className="pxl-adm-code">{aff.referralCode}</code></td>
                                        <td><p className="pxl-ud-amount">${Number(aff.currentBalance || 0).toFixed(2)}</p></td>
                                        <td><p>${Number(aff.totalEarnings || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p></td>
                                        <td>
                                            <span className={`pxl-ud-pill ${
                                                aff.status === 'approved' ? 'pxl-ud-pill--success'
                                                : aff.status === 'suspended' ? 'pxl-ud-pill--danger'
                                                : 'pxl-ud-pill--muted'
                                            }`}>
                                                {aff.status}
                                            </span>
                                        </td>
                                        <td>
                                            <button className="pxl-adm-pay-btn" disabled={aff.status !== 'approved'}>
                                                Pay
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={7}><EmptyRow text="No affiliates with a balance." /></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </SectionPanel>
        </div>
    );
}

// ── Main component ─────────────────────────────────────────────────────────────
const DASHBOARD_TABS = ['Users', 'Apps', 'Billing', 'Affiliates'];

const AdminDashboard = ({ pageTitle }) => {
    const store = useStore(s => s);
    const [activeTab, setActiveTab] = useState(0);

    // ── page-level range filter ──
    const [range, setRange] = useState('30d');
    const [customFrom, setCustomFrom] = useState(null);
    const [customTo,   setCustomTo]   = useState(null);

    // ── dashboard data ──
    const [data, setData] = useState(null);
    const [refreshing,  setRefreshing]  = useState(false);
    const [dismissedAlerts, setDismissedAlerts] = useState([]);

    const fetchDashboard = (silent = false) => {
        if (!silent) setRefreshing(true);
        const params = new URLSearchParams();
        params.set('range', range);
        if (range === 'custom') {
            if (customFrom) params.set('from', customFrom.toISOString());
            if (customTo)   params.set('to',   customTo.toISOString());
        }
        hitApi(
            { url: `admin/dashboard?${params.toString()}`, method: 'GET', data: {}, alert: false },
            (resp) => {
                setRefreshing(false);
                if (resp?.status) {
                    setData(resp.data);
                }
            }
        );
    };

    // initial load + reload when range changes
    useEffect(() => {
        // for custom: only fetch when both bounds chosen
        if (range === 'custom' && (!customFrom || !customTo)) return;
        fetchDashboard(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, customFrom, customTo]);

    const handleRefresh = () => fetchDashboard(false);

    // ── alerts (from backend payload; map tones + targets to UI shape) ──
    const tabIndexByName = { users: 0, apps: 1, billing: 2, affiliates: 3 };
    const allAlerts = (data?.alerts || [])
        .filter(a => !dismissedAlerts.includes(a.id))
        .map(a => ({
            id: a.id,
            tone: a.tone,
            text: a.text,
            to:   a.target?.type === 'route' ? a.target.value : undefined,
            onClick: a.target?.type === 'tab'
                ? () => setActiveTab(tabIndexByName[a.target.value] ?? 0)
                : undefined,
        }));

    return (
        <>
            <Helmet><title>{pageTitle}</title></Helmet>
            <div className="pxl_mainWrapper">
                <div className="pxl_rightBar_assets">

                    <div className="pxl-rb-header-wrap">
                        <div className="pxl-headingTop" />
                        <div className="pxl-rb-header-right">
                            <div className="pxl-header-toogle-btn"
                                onClick={() => store.updateStore('resMenuToggle', !store.resMenuToggle)}>
                                <svg className="pxl-header-toogle-svg" viewBox="0 0 100 100" width="80">
                                    <path className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-top"
                                        d="m 30,33 h 40 c 3.722839,0 7.5,3.126468 7.5,8.578427 0,5.451959 -2.727029,8.421573 -7.5,8.421573 h -20" />
                                    <path className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-middle" d="m 30,50 h 40" />
                                    <path className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-bottom"
                                        d="m 70,67 h -40 c 0,0 -7.5,-0.802118 -7.5,-8.365747 0,-7.563629 7.5,-8.634253 7.5,-8.634253 h 20" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <DashboardHeader
                        title="Dashboard"
                        subtitle="Overview of users, billing & affiliates"
                        range={range} setRange={setRange}
                        customFrom={customFrom} setCustomFrom={setCustomFrom}
                        customTo={customTo}     setCustomTo={setCustomTo}
                        onRefresh={handleRefresh}
                        refreshing={refreshing}
                    />

                    <AlertsBanner
                        alerts={allAlerts}
                        onDismiss={(id) => setDismissedAlerts(prev => [...prev, id])}
                    />

                    <div className="pxl-ud-tabs pxl-adm-dash-tabs">
                        {DASHBOARD_TABS.map((t, i) => (
                            <button
                                key={i}
                                className={`pxl-ud-tab-btn${activeTab === i ? ' is-active' : ''}`}
                                onClick={() => setActiveTab(i)}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    {activeTab === 0 && <UsersTab      data={data?.users} />}
                    {activeTab === 1 && <AppsTab       apps={data?.apps} />}
                    {activeTab === 2 && <BillingTab    data={data?.billing} />}
                    {activeTab === 3 && <AffiliatesTab data={data?.affiliates} />}

                </div>
            </div>
        </>
    );
};

export default AdminDashboard;
